/** Constant-product bonding curve in integer units (lamports / raw token units), BigInt math. */
import { SIM } from './config';

/** lamports, raw token units. feeBps: pool fee (bonding curve 1%; post-migration AMM 0.25%). */
export interface Reserves { vSol: number; vTok: number; feeBps?: number }

const L = SIM.LAMPORTS;
const RAW = 10 ** SIM.TOKEN_DECIMALS;
export const INITIAL: Reserves = { vSol: SIM.V_SOL0 * L, vTok: SIM.V_TOK0 * RAW };

export const isInt = (n: number) => Number.isSafeInteger(n) && n >= 0;

export function feeOf(lamports: number, bps: number = SIM.TRADE_FEE_BPS): number { return Math.floor((lamports * bps) / 10_000); }

/** Tokens out for a buy of `solIn` gross lamports. Fee is deducted first. */
export function quoteBuy(r: Reserves, solIn: number): { tokensOut: number; fee: number; next: Reserves } {
  if (!isInt(solIn) || solIn <= 0) throw new Error('invalid_amount');
  const fee = feeOf(solIn, r.feeBps);
  const net = solIn - fee;
  const out = Number((BigInt(r.vTok) * BigInt(net)) / (BigInt(r.vSol) + BigInt(net)));
  return { tokensOut: out, fee, next: { ...r, vSol: r.vSol + net, vTok: r.vTok - out } };
}

/** SOL out (net of fee) for selling `tokIn` raw units. */
export function quoteSell(r: Reserves, tokIn: number): { solOut: number; fee: number; next: Reserves } {
  if (!isInt(tokIn) || tokIn <= 0) throw new Error('invalid_amount');
  const gross = Number((BigInt(r.vSol) * BigInt(tokIn)) / (BigInt(r.vTok) + BigInt(tokIn)));
  const fee = feeOf(gross, r.feeBps);
  return { solOut: gross - fee, fee, next: { ...r, vSol: r.vSol - gross, vTok: r.vTok + tokIn } };
}

/** Price in SOL per whole token. */
export function priceSol(r: Reserves): number { return (r.vSol / L) / (r.vTok / RAW); }
export function mcSol(r: Reserves): number { return priceSol(r) * (SIM.TOTAL_SUPPLY_RAW / RAW); }
export function mcUsd(r: Reserves): number { return mcSol(r) * SIM.SOL_USD; }

/**
 * Curve completion is defined by supply, not a price target: 793.1M of the 1.073B virtual tokens are sellable on the
 * curve. At completion vTok = 279.9M, vSol = 30 * 1073 / 279.9 = 115.0 SOL, so MC = 115.0 / 279.9M * 1B = 410.9 SOL
 * = $61.6K at $150 (derived; replaces the old hard-coded $100K).
 */
const TOK_K = SIM.V_SOL0 * SIM.V_TOK0;
export const V_TOK_END = SIM.V_TOK0 - SIM.TOKENS_ON_CURVE;
export const V_END_SOL = TOK_K / V_TOK_END;
export const MIGRATION_MC_USD = (V_END_SOL / V_TOK_END) * 1e9 * SIM.SOL_USD;
/** Bonding progress 0..100 from reserves (tokens sold / tokens on curve). */
export function bonding(r: Reserves): number {
  if (r.feeBps === SIM.AMM_FEE_BPS) return 100;
  const sold = SIM.V_TOK0 - r.vTok / RAW;
  return Math.max(0, Math.min(100, (sold / SIM.TOKENS_ON_CURVE) * 100));
}
/** Post-migration AMM pool: real reserves only (no virtual), seeded with ~79 SOL and the unsold supply (~206.9M). */
export function ammPool(unsoldRaw: number): Reserves {
  return { vSol: Math.round(SIM.AMM_SEED_SOL * L * (unsoldRaw / (SIM.AMM_SEED_TOKENS * RAW))), vTok: unsoldRaw, feeBps: SIM.AMM_FEE_BPS };
}
export const solToLamports = (sol: number) => Math.round(sol * L);
export const lamportsToSol = (l: number) => l / L;
export const rawToTokens = (r: number) => r / RAW;
export const tokensToRaw = (t: number) => Math.round(t * RAW);
